/**
 * The Calls tab's pure helpers (docs/plans/oncall-line-plan.md 5, 7.6, 7.7,
 * 7.8; WP-12): what each call status, review state and attempt outcome is
 * called and coloured, the unreviewed-first order, the attempt timeline's
 * waves and the transcript's speaker runs. No React and no network, so the
 * list, the drawer and their tests read the same words.
 *
 * Colours are data colours (the status of a call), always paired with a word
 * and an icon (the design system: colour never carries meaning alone).
 */
import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Voicemail,
} from 'lucide-react';
import type {
  OncallAttemptOutcome,
  OncallCallAttempt,
  OncallCallDirection,
  OncallCallStatus,
  OncallCallSummary,
  OncallReviewStatus,
  OncallTrack,
  OncallTranscriptSegment,
} from '../../api/oncallApi';

/** The list is asked again every 60 s while the page is visible (plan 7.7: polling, not SSE). */
export const CALLS_POLL_MS = 60_000;

/** Rows per page of the call log (the server's default; it accepts up to 200). */
export const CALLS_PAGE_SIZE = 50;

/** The server's cap on one page (routes/oncall/calls.js MAX_PAGE_LIMIT). */
export const CALLS_MAX_PAGE = 200;

/** The review note's limit (routes/oncall/calls.js MAX_REVIEW_NOTE_LENGTH). */
export const REVIEW_NOTE_MAX = 4000;

// ---------------------------------------------------------------------------
// Pills
// ---------------------------------------------------------------------------

/** One pill's look: its words, its tooltip, its colour family and icon. */
export interface PillMeta {
  label: string;
  /** A longer explanation for the title attribute. */
  hint: string;
  /** The status-pill colour classes (bg-{c}-100 text-{c}-800 + dark). */
  cls: string;
  Icon: LucideIcon;
}

const GREEN = 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
const AMBER = 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
const RED = 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
const BLUE = 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300';
const GRAY = 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300';

export const STATUS_META: Record<OncallCallStatus, PillMeta> = {
  ringing: { label: 'In progress', hint: 'The call is still ringing or connected', cls: BLUE, Icon: PhoneCall },
  answered: { label: 'Answered', hint: 'A team member pressed 1 and took the call', cls: GREEN, Icon: CheckCircle2 },
  voicemail: { label: 'Voicemail', hint: 'Nobody took the call and the caller left a voicemail', cls: AMBER, Icon: Voicemail },
  missed: { label: 'Missed', hint: 'Nobody took the call and no voicemail was left', cls: RED, Icon: PhoneMissed },
  abandoned: { label: 'Abandoned', hint: 'The caller hung up before anyone answered', cls: RED, Icon: PhoneOff },
  failed: { label: 'Failed', hint: 'The call did not connect', cls: RED, Icon: AlertTriangle },
  completed: { label: 'Completed', hint: 'The call finished', cls: GREEN, Icon: Check },
};

/** A status the server may add later still renders (gray, its own word). */
export function statusMeta(status: string | null | undefined): PillMeta {
  if (status && Object.prototype.hasOwnProperty.call(STATUS_META, status)) {
    return STATUS_META[status as OncallCallStatus];
  }
  return { label: status || 'Unknown', hint: 'Unknown status', cls: GRAY, Icon: Clock };
}

export const REVIEW_META: Record<OncallReviewStatus, PillMeta> = {
  new: {
    label: 'Needs review',
    hint: 'Not yet documented: file what the caller reported in the participant record, then mark it reviewed',
    cls: BLUE,
    Icon: Clock,
  },
  reviewed: { label: 'Reviewed', hint: 'Documented and marked reviewed', cls: GRAY, Icon: Check },
};

export function reviewMeta(review: string | null | undefined): PillMeta {
  return review === 'reviewed' ? REVIEW_META.reviewed : REVIEW_META.new;
}

export interface DirectionMeta {
  /** The short word on a list row. */
  label: string;
  /** The drawer's heading. */
  title: string;
  Icon: LucideIcon;
}

export const DIRECTION_META: Record<OncallCallDirection, DirectionMeta> = {
  inbound: { label: 'Incoming', title: 'Incoming call', Icon: PhoneIncoming },
  callback: { label: 'Callback', title: 'Callback', Icon: PhoneOutgoing },
  test: { label: 'Test call', title: 'Test call', Icon: PhoneCall },
  heartbeat: { label: 'Heartbeat', title: 'Heartbeat call', Icon: PhoneCall },
};

export function directionMeta(direction: string | null | undefined): DirectionMeta {
  if (direction && Object.prototype.hasOwnProperty.call(DIRECTION_META, direction)) {
    return DIRECTION_META[direction as OncallCallDirection];
  }
  return { label: 'Call', title: 'Call', Icon: PhoneCall };
}

// ---------------------------------------------------------------------------
// Attempts (the timeline)
// ---------------------------------------------------------------------------

export interface OutcomeMeta {
  label: string;
  hint: string;
  /** Text colour for the outcome word. */
  cls: string;
}

export const OUTCOME_META: Record<OncallAttemptOutcome, OutcomeMeta> = {
  accepted: { label: 'Accepted', hint: 'Pressed 1 and took the call', cls: 'text-green-700 dark:text-green-400' },
  'no-answer': { label: 'No answer', hint: 'Rang without being picked up', cls: 'text-gray-600 dark:text-gray-300' },
  busy: { label: 'Busy', hint: 'The phone was busy', cls: 'text-gray-600 dark:text-gray-300' },
  failed: { label: 'Failed', hint: 'The phone could not be reached', cls: 'text-red-700 dark:text-red-400' },
  'screened-out': {
    label: 'No key pressed',
    hint: 'The phone answered but nobody pressed 1 (usually a voicemail box), so it was hung up',
    cls: 'text-amber-700 dark:text-amber-400',
  },
  canceled: {
    label: 'Stopped',
    hint: 'Stopped ringing because the call was taken or ended',
    cls: 'text-gray-500 dark:text-gray-400',
  },
};

export function outcomeMeta(outcome: string | null | undefined, callStillRinging = false): OutcomeMeta {
  if (outcome && Object.prototype.hasOwnProperty.call(OUTCOME_META, outcome)) {
    return OUTCOME_META[outcome as OncallAttemptOutcome];
  }
  if (outcome) return { label: outcome, hint: outcome, cls: 'text-gray-600 dark:text-gray-300' };
  return callStillRinging
    ? { label: 'Ringing', hint: 'Still ringing', cls: 'text-primary-700 dark:text-primary-400' }
    : { label: 'No result', hint: 'No outcome was recorded for this phone', cls: 'text-gray-500 dark:text-gray-400' };
}

/** One wave of the hunt: the phones rung together (wave 0 is the backup phones). */
export interface AttemptWave {
  waveNo: number;
  /** "Wave 1", or "Backup phones" for wave 0 (the fallback numbers, plan 4 layer 3). */
  label: string;
  /** The earliest ring of the wave, or null. */
  rangAt: string | null;
  attempts: OncallCallAttempt[];
}

function time(value: string | null | undefined): number {
  const ms = value ? Date.parse(value) : NaN;
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * The attempts grouped by wave, in the order they rang: waves by first ring
 * (wave number breaking ties), phones in a wave by attempt number. A hunt
 * that reached the backup phones after the waves shows them last because
 * they rang last.
 */
export function groupAttempts(attempts: OncallCallAttempt[] | null | undefined): AttemptWave[] {
  const byWave = new Map<number, OncallCallAttempt[]>();
  for (const a of attempts ?? []) {
    const wave = Number.isFinite(a.wave_no) ? a.wave_no : 0;
    const list = byWave.get(wave) ?? [];
    list.push(a);
    byWave.set(wave, list);
  }
  const waves: AttemptWave[] = [];
  byWave.forEach((list, waveNo) => {
    const sorted = [...list].sort((x, y) => (x.attempt_no ?? 0) - (y.attempt_no ?? 0));
    const first = sorted.reduce<string | null>((earliest, a) => {
      if (!a.rang_at) return earliest;
      return earliest === null || time(a.rang_at) < time(earliest) ? a.rang_at : earliest;
    }, null);
    waves.push({ waveNo, label: waveNo === 0 ? 'Backup phones' : `Wave ${waveNo}`, rangAt: first, attempts: sorted });
  });
  return waves.sort((x, y) => time(x.rangAt) - time(y.rangAt) || x.waveNo - y.waveNo);
}

// ---------------------------------------------------------------------------
// The transcript (two voices)
// ---------------------------------------------------------------------------

export const TRACK_LABEL: Record<OncallTrack, string> = { caller: 'Caller', staff: 'Staff' };

/** Consecutive segments from one speaker, shown as one bubble. */
export interface TranscriptRun {
  track: OncallTrack;
  /** The first segment's time. */
  at: string | null;
  lines: string[];
}

/**
 * The server's merged transcript (7.8 rule 3, already in order) folded into
 * speaker runs, so the drawer reads as a conversation. Empty text is skipped;
 * an unknown track reads as the caller's side only if it says 'caller'.
 */
export function transcriptRuns(segments: OncallTranscriptSegment[] | null | undefined): TranscriptRun[] {
  const runs: TranscriptRun[] = [];
  for (const s of segments ?? []) {
    const text = typeof s.text === 'string' ? s.text.trim() : '';
    if (!text) continue;
    const track: OncallTrack = s.track === 'caller' ? 'caller' : 'staff';
    const last = runs[runs.length - 1];
    if (last && last.track === track) {
      last.lines.push(text);
    } else {
      runs.push({ track, at: s.created_at ?? null, lines: [text] });
    }
  }
  return runs;
}

// ---------------------------------------------------------------------------
// The list order
// ---------------------------------------------------------------------------

/**
 * Unreviewed calls first, then newest first, then id: the server's own order
 * (routes/oncall/calls.js), reapplied on the client so a row reviewed in the
 * drawer moves at once rather than at the next poll.
 */
export function sortCalls<T extends Pick<OncallCallSummary, 'id' | 'review_status' | 'started_at'>>(rows: T[]): T[] {
  const rank = (r: T) => (r.review_status === 'reviewed' ? 1 : 0);
  return [...rows].sort((a, b) => {
    const byReview = rank(a) - rank(b);
    if (byReview) return byReview;
    const at = Date.parse(a.started_at ?? '') || 0;
    const bt = Date.parse(b.started_at ?? '') || 0;
    if (at !== bt) return bt - at;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** Replace rows by id (a reviewed call's new state), keeping the rest. */
export function mergeCall<T extends { id: string }>(rows: T[], changed: Partial<T> & { id: string }): T[] {
  return rows.map((r) => (r.id === changed.id ? { ...r, ...changed } : r));
}

// ---------------------------------------------------------------------------
// Page visibility
// ---------------------------------------------------------------------------

/** True unless the document says it is hidden (no document: visible). */
export function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}
